import React, { useState } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { Button, TextField, Typography, Alert, CircularProgress, Divider, useTheme } from '@mui/material';

/**
 * ForgotPassword component handles the Cognito password reset flow:
 * 1. User enters their email to receive a verification code
 * 2. User enters the code + new password to reset
 *
 * Props:
 * - onBack: callback to return to login
 * - initialEmail: pre-fill email (e.g. from login form)
 * - codeAlreadySent: if true, skip straight to the code entry step
 *   (used when Cognito has already sent the code, e.g. admin-triggered password reset)
 * - expiredTempPassword: if true, show a contextual message about the expired temporary password
 */
function ForgotPassword({ onBack, initialEmail = '', codeAlreadySent = false, expiredTempPassword = false }) {
  const [step, setStep] = useState(codeAlreadySent ? 'confirm' : 'request');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResendTempBtn, setShowResendTempBtn] = useState(false);
  const theme = useTheme();

  const handleResendTempPassword = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    if (!email.trim()) {
      setErrorMessage('Please enter your email address first.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_AWS_API_EXTERNAL_URL}/auth/resend_temp_password`,
        { email: email.trim() }
      );
      setSuccessMessage('A new temporary password has been sent to your email. Please check your inbox and use it to log in.');
    } catch (err) {
      console.error('Resend temp password error:', err);
      const serverMsg = (err.response?.data?.message || err.response?.data?.error || '').toLowerCase();
      if (serverMsg.includes('not in a state')) {
        setErrorMessage('This account does not require a temporary password reset. Please use the "Forgot Password" flow above, or contact the Customer Team if you need further help.');
      } else {
        setErrorMessage('Failed to resend temporary password. Please contact Customer Team for assistance.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      await Auth.forgotPassword(email.trim());
      setSuccessMessage('A verification code has been sent to your email/phone.');
      setStep('confirm');
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.code === 'UserNotFoundException') {
        setErrorMessage('No account found with that email.');
      } else if (error.code === 'LimitExceededException') {
        setErrorMessage('Too many attempts. Please try again later.');
      } else if (
        error.code === 'NotAuthorizedException' &&
        (error.message || '').toLowerCase().includes('cannot be reset in the current state')
      ) {
        setErrorMessage(
          'Your account has not yet been activated. Please use your temporary password to log in, or resend it below.'
        );
        setShowResendTempBtn(true);
      } else {
        setErrorMessage(error.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 16) {
      setErrorMessage('Password must be at least 16 characters long.');
      return;
    }

    setLoading(true);

    try {
      await Auth.forgotPasswordSubmit(email.trim(), code, newPassword);
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
      setTimeout(() => onBack(), 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.code === 'CodeMismatchException') {
        setErrorMessage('Invalid verification code.');
      } else if (error.code === 'ExpiredCodeException') {
        setErrorMessage('Verification code has expired. Please request a new one.');
      } else if (error.code === 'InvalidPasswordException') {
        setErrorMessage(error.message || 'Password does not meet requirements.');
      } else {
        setErrorMessage(error.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '280px' }}>
      <Typography variant="h6" style={{ textAlign: 'center' }}>
        Reset Password
      </Typography>

      {errorMessage && (
        <Alert severity="error">{errorMessage}</Alert>
      )}
      {successMessage && (
        <Alert severity="success">{successMessage}</Alert>
      )}

      {step === 'request' ? (
        <form onSubmit={handleRequestCode} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <Typography variant="body2" style={{ textAlign: 'center' }}>
            Enter your email address and we&apos;ll send you a verification code to reset your password.
          </Typography>
          <TextField
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setShowResendTempBtn(false); }}
            required
            disabled={loading}
          />
          <Button type="submit" color="primary" variant="contained" size="large" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
          </Button>
          {showResendTempBtn && (
            <>
              <Divider style={{ margin: '4px 0' }}>or</Divider>
              <Button
                onClick={handleResendTempPassword}
                variant="outlined"
                style={{ color: theme.palette.text.primary, width: '280px' }}
                size="small"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Resend Temporary Password'}
              </Button>
            </>
          )}
          <Button onClick={onBack} color="inherit" size="small">
            Back to Login
          </Button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {expiredTempPassword && (
            <Alert severity="warning">
              Your temporary password has expired. A verification code has been sent to your email/phone. Enter the code below along with your new password.
            </Alert>
          )}
          <Typography variant="body2" style={{ textAlign: 'center' }}>
            {codeAlreadySent
              ? 'A password reset code has been sent to your email/phone. Enter it below with your new password.'
              : 'Enter the verification code sent to your email/phone and your new password.'}
          </Typography>
          <TextField
            label="Verification Code"
            variant="outlined"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={loading}
          />
          <TextField
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={() => setShowNewPassword((show) => !show)}
                  size="small"
                  tabIndex={-1}
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </Button>
              ),
            }}
          />
          <TextField
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={() => setShowConfirmPassword((show) => !show)}
                  size="small"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
              ),
            }}
          />
          <Button type="submit" color="primary" variant="contained" size="large" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
          <Button onClick={() => setStep('request')} color="inherit" size="small">
            Resend Code
          </Button>
          <Button onClick={onBack} color="inherit" size="small">
            Back to Login
          </Button>
        </form>
      )}
    </div>
  );
}

export default ForgotPassword;
